import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '#app/utils/misc.tsx'

const buttonVariants = cva(
	'inline-flex min-h-[44px] items-center justify-center rounded-sm border text-sm ring-offset-background outline-hidden transition-all duration-200 focus-within:ring-2 focus-visible:ring-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
	{
		variants: {
			variant: {
				default:
					'cathedral-heading cathedral-cta-primary hover:-translate-y-[1px] hover:brightness-105 focus-visible:ring-cathedral-bright tracking-[0.13em] uppercase',
				destructive:
					'bg-destructive text-destructive-foreground hover:bg-destructive/80',
				outline:
					'cathedral-cta-secondary cathedral-heading focus-visible:ring-cathedral-bright tracking-[0.12em] uppercase hover:bg-cathedral-bright/8 hover:text-cathedral-dark',
				secondary:
					'bg-cathedral-surface border-cathedral-border cathedral-heading text-cathedral-amber tracking-[0.12em] uppercase hover:border-cathedral-bright hover:text-cathedral-dark focus-visible:ring-cathedral-bright',
				ghost:
					'cathedral-heading text-cathedral-amber tracking-[0.1em] uppercase hover:bg-cathedral-bright/8 hover:text-cathedral-dark',
				link: 'cathedral-body text-cathedral-gold underline-offset-4 hover:underline',
			},
			size: {
				default: 'px-4 py-2.5',
				wide: 'px-10 py-4',
				sm: 'px-3 py-2 text-xs',
				lg: 'px-8 py-3.5',
				pill: 'rounded-full px-12 py-3 leading-3',
				icon: 'size-10',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
		},
	},
)

export type ButtonVariant = VariantProps<typeof buttonVariants>

const Button = ({
	className,
	variant,
	size,
	asChild = false,
	...props
}: React.ComponentProps<'button'> &
	ButtonVariant & {
		asChild?: boolean
	}) => {
	const Comp = asChild ? Slot : 'button'
	return (
		<Comp
			data-slot="button"
			className={cn(buttonVariants({ variant, size, className }))}
			{...props}
		/>
	)
}

export { Button, buttonVariants }
